import { Box, CssBaseline, Typography } from "@mui/material";
import { History as RunsIcon, MenuBook as PlaybooksIcon } from "@mui/icons-material";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import ConsoleSidebar, { type ConsoleNavEntry, type ConsoleSidebarPalette } from "../console/ConsoleSidebar";
import lensMark from "./assets/lens-mark.svg";
import Playbooks from "./Playbooks";
import PlaybookEditor from "./PlaybookEditor";
import Runs from "./Runs";
import RunDetail from "./RunDetail";

type LensArea = "playbooks" | "runs";

const NAV: ConsoleNavEntry<LensArea>[] = [
    { key: "playbooks", label: "Playbooks", icon: <PlaybooksIcon /> },
    { key: "runs", label: "Runs", icon: <RunsIcon /> },
];

const PALETTE: ConsoleSidebarPalette = {
    background: "#ffffff",
    text: "#12054D",
    muted: "#6e6577",
    hover: "#f2edff",
    activeText: "#5c45c7",
    accent: "#5c45c7",
    border: "#C6C6C6",
};

const brand = (
    <>
        <Box component="img" src={lensMark} alt="" sx={{ width: 32, height: 32, display: "block", mb: "10px" }} />
        <Typography sx={{ fontWeight: 800, fontSize: "19px", letterSpacing: "-0.01em", color: PALETTE.text }}>Lens</Typography>
        <Typography
            sx={{ fontSize: "11px", color: PALETTE.muted, mt: "2px", letterSpacing: "0.06em", textTransform: "uppercase" }}
        >
            WorkNest Tools · Contract review
        </Typography>
    </>
);

const LensConsole = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const active: LensArea = pathname.startsWith("/lens/runs") ? "runs" : "playbooks";

    return (
        <Box sx={{ display: "flex", height: "100vh", width: "100%", overflow: "hidden" }}>
            <CssBaseline />
            <ConsoleSidebar
                brand={brand}
                onBrandClick={() => navigate("/lens/playbooks")}
                items={NAV}
                active={active}
                onNavigate={(area) => navigate(`/lens/${area}`)}
                palette={PALETTE}
            />

            {/* Mirrors DashboardLayout's MainContent, which every Lens page was laid out inside. */}
            <Box
                component="main"
                sx={(theme) => ({
                    flex: 1,
                    minWidth: 0,
                    overflowY: "auto",
                    background: theme.body.background,
                    padding: `${theme.spacing(1)} ${theme.spacing(5)}`,
                    "& .MuiPaper-root .MuiPaper-root": { boxShadow: "none" },
                })}
            >
                <Routes>
                    <Route path="playbooks" element={<Playbooks />} />
                    <Route path="playbooks/:key" element={<PlaybookEditor />} />
                    <Route path="runs" element={<Runs />} />
                    <Route path="runs/:id" element={<RunDetail />} />
                    <Route path="*" element={<Navigate to="/lens/playbooks" replace />} />
                </Routes>
            </Box>
        </Box>
    );
};

export default LensConsole;
